import type {LucideIcon} from "lucide-react";
import {
    LayoutDashboard, Users, Clock, ClipboardList,
    Settings, FileText, UserPlus, UserSearch,
    FileUser, CalendarDays, PartyPopper, Gift,
    Clapperboard, Combine, FolderClock, FolderOpen,
    FolderKanban, FolderGit2, CalendarClock, CalendarCheck, BookSearch, User, ChartColumnBig,
} from "lucide-react";
import {RoleList, UserRole} from "../../../core/roles/roles.type";


export type RolePathMap = Partial<Record<UserRole, string>>;
export type PathConfig = string | RolePathMap;

export interface NavLeafItem {
    id: string;
    label: string;
    icon: LucideIcon;
    path: PathConfig;
    roles: RoleList;
    badge?: string;
    children?: never;
}

export interface NavParentItem {
    id: string;
    label: string;
    icon: LucideIcon;
    roles: RoleList;
    children: NavItem[];
    path?: never;
    badge?: never;
}

export type NavItem = NavLeafItem | NavParentItem;

export interface NavSection {
    label: string;
    items: NavItem[];
}

const ALL: RoleList = ["ADMIN", "EMPLOYEE"];
const ADM: RoleList = ["ADMIN"];
const EMP: RoleList = ["EMPLOYEE"];

export const NAV_SECTIONS: NavSection[] = [
    {
        label: "Main",
        items: [
            {
                id: "dashboard",
                label: "Dashboard",
                icon: LayoutDashboard,
                path: "/dashboard",
                roles: ALL,
            }
        ],
    },
    {
        label: "People Management",
        items: [
            {
                id: "users",
                label: "User",
                icon: User,
                roles: ADM,
                children: [
                    {
                        id: "user-list",
                        label: "List",
                        icon: FileUser,
                        path: "/user/list",
                        roles: ADM,
                    }
                ],
            },
            {
                id: "employee",
                label: "Employee",
                icon: Users,
                roles: ALL,
                children: [
                    {
                        id: "employee-search",
                        label: "Search",
                        icon: UserSearch,
                        path: "/employee/search",
                        roles: ALL,
                    },
                    {
                        id: "attendance",
                        label: "Attendance",
                        icon: ClipboardList,
                        path: {
                            ADMIN: "/employee/attendance",
                            EMPLOYEE: "/my/attendance",
                        },
                        roles: ALL,
                    },
                ],
            }
        ],
    },
    {
        label: "Workspace",
        items: [
            {
                id: "time-off",
                label: "Time Off",
                icon: CalendarClock,
                roles: ALL,
                children: [
                    {
                        id: "leaves",
                        label: "Leaves",
                        icon: CalendarCheck,
                        path: "/time-off/leaves/summary",
                        roles: ALL,
                    },
                    {
                        id: "holidays",
                        label: "Holidays",
                        icon: CalendarDays,
                        path: "/time-off/holidays",
                        roles: ALL,
                    },
                ],
            },
            {
                id: "policy",
                label: "Policy",
                icon: FileText,
                path: "/policy",
                roles: ALL,
            },
            {
                id: "poll",
                label: "Polls & Votes",
                icon: ChartColumnBig,
                path: "/poll",
                roles: ALL,
            },
        ],
    },
    {
        label: "Asset Management",
        items: [
            {
                id: "assets",
                label: "Assets",
                icon: FolderOpen,
                roles: ALL,
                children: [
                    {
                        id: "asset-list",
                        label: "Analysis",
                        icon: FolderKanban,
                        path: "/asset/dashboard",
                        roles: ADM,
                    },
                    {
                        id: "asset-category",
                        label: "Categories",
                        icon: FolderGit2,
                        path: "/asset/category",
                        roles: ADM,
                    },
                    {
                        id: "asset-inventory",
                        label: "Asset Inventory",
                        icon: BookSearch,
                        path: "/asset/inventory",
                        roles: ADM,
                    },
                    {
                        id: "asset-allocate",
                        label: "Allocation & Approval",
                        icon: Combine,
                        path: "/asset/allocation",
                        roles: ADM,
                    },
                    {
                        id: "asset-allocate-requested",
                        label: "Allocated & Requested",
                        icon: Combine,
                        path: "/asset/allocation",
                        roles: EMP,
                    },
                ],
            },
        ],
    },
    {
        label: "Highlights",
        items: [
            {
                id: "events",
                label: "Events",
                icon: PartyPopper,
                path: "/highlights/our-events",
                roles: ALL,
            },
            {
                id: "celebration",
                label: "Celebrations",
                icon: Gift,
                path: "/highlights/celebrations",
                roles: ALL,
            },
            {
                id: "media-hub",
                label: "Media Hub",
                icon: Clapperboard,
                path: "/highlights/media-hub",
                roles: ALL,
            },
        ],
    },
    {
        label: "System",
        items: [
            {
                id: "settings",
                label: "Settings",
                icon: Settings,
                path: "/setting/leave-types",
                roles: ADM
            }
        ],
    },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helper functions
// ─────────────────────────────────────────────────────────────────────────────

export function resolvePath(pathConfig: PathConfig, role: UserRole): string {
    if (typeof pathConfig === "string") return pathConfig;
    return pathConfig[role] ?? Object.values(pathConfig)[0] ?? "/";
}

export function filterNavByRole(sections: NavSection[], role: UserRole): NavSection[] {
    return sections
        .map(section => ({
            ...section,
            items: section.items
                .filter(item => item.roles.includes(role))
                .map(item => {
                    if (item.children) {
                        return {
                            ...item,
                            children: item.children.filter(child => child.roles.includes(role)),
                        };
                    }
                    return item;
                })
                .filter(item => !item.children || item.children.length > 0),
        }))
        .filter(section => section.items.length > 0);
}
